import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { MultiSelect } from "react-multi-select-component";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AgencyPerformanceChart = ({ data }) => {
  const [selectedMetrics, setSelectedMetrics] = useState([
    { label: "Total RFQ Received", value: "totalRfqReceived" },
    { label: "Total RFQ Responses", value: "totalRfqResponses" }
  ]);

  // Available metrics for selection
  const availableMetrics = [
    { label: "Total PO", value: "totalPO" },
    { label: "Total RFQ Received", value: "totalRfqReceived" },
    { label: "Total RFQ Responses", value: "totalRfqResponses" },
    // { label: "Total PO Cost", value: "totalPoCost" },
    // { label: "Total RFQ Cost", value: "totalRfqCost" },
    // { label: "Projected Margin", value: "projMargin" },
    // { label: "Total Final PO Cost", value: "totalFinalPoCost" },
    // { label: "Actual Margin", value: "actualMargin" },
    // { label: "Taxes", value: "taxes" },
    // { label: "Profit", value: "profit" },
    { label: "VPO Invoices", value: "vpoInvoices" },
    { label: "Total SPO Invoices", value: "totalSpoInvoices" },
    // { label: "Buying Price", value: "buyingPrice" }
  ];

  // Process data for chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    const agencies = data.map(item => item.agency);
    const datasets = selectedMetrics.map((metric, index) => {
      const values = data.map(item => item[metric.value] || 0);
      
      // Generate colors for each dataset using more distinct shades of #1e2b37
      const colors = [
        '#1e2b37', // Base color (darkest)
        '#2c3e50', // Dark blue-gray
        '#34495e', // Medium dark
        '#5d6d7e', // Medium gray
        '#7f8c8d', // Light gray
        '#95a5a6', // Lighter gray
        '#bdc3c7', // Very light gray
        '#d5dbdb', // Almost white
        '#ecf0f1', // Very pale
        '#f8f9fa', // Nearly white
        '#e8f4fd', // Light blue tint
        '#d1ecf1', // Pale blue
        '#bee5eb'  // Lightest blue
      ];

      return {
        label: metric.label,
        data: values,
        backgroundColor: colors[index % colors.length],
        borderColor: colors[index % colors.length],
        borderWidth: 1,
        borderRadius: 0,
        borderSkipped: false,
      };
    });

    return {
      labels: agencies,
      datasets: datasets
    };
  }, [data, selectedMetrics]);

  // Calculate minimum width for chart when there are many agencies
  const chartMinWidth = useMemo(() => {
    if (!data || data.length === 0) return '100%';
    
    // If more than 8 agencies, set a minimum width to enable scrolling
    if (data.length > 8) {
      // Calculate width: 80px per agency with a reasonable minimum
      const calculatedWidth = Math.max(data.length * 80, 800);
      return `${calculatedWidth}px`;
    }
    
    return '100%';
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            family: 'Inter, sans-serif'
          }
        }
      },
      title: {
        display: true,
        text: 'Agency Performance Comparison',
        font: {
          size: 16,
          weight: 'bold',
          family: 'Inter, sans-serif'
        },
        color: '#374151'
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            const label = context.dataset.label;
            
            // Format numbers with commas for better readability
            if (typeof value === 'number') {
              if (value >= 1000000) {
                return `${label}: ${(value / 1000000).toFixed(1)}M`;
              } else if (value >= 1000) {
                return `${label}: ${(value / 1000).toFixed(1)}K`;
              } else {
                return `${label}: ${value.toLocaleString()}`;
              }
            }
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif'
          },
          color: '#6B7280',
          maxRotation: 45,
          minRotation: 0
        },
        title: {
          display: true,
          text: 'Agencies',
          font: {
            size: 12,
            weight: 'bold',
            family: 'Inter, sans-serif'
          },
          color: '#374151'
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#E5E7EB',
          drawBorder: false
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif'
          },
          color: '#6B7280',
          callback: function(value) {
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'K';
            }
            return value.toLocaleString();
          }
        },
        title: {
          display: true,
          text: 'Values',
          font: {
            size: 12,
            weight: 'bold',
            family: 'Inter, sans-serif'
          },
          color: '#374151'
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart'
    }
  };

  return (
    <div className="reports-card">
      <div className="reports-filter-controls">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="reports-filter-label">Select Metrics:</label>
            <MultiSelect
              options={availableMetrics}
              value={selectedMetrics}
              onChange={setSelectedMetrics}
              labelledBy="Select Metrics"
              className='reports-multiselect'
              hasSelectAll={true}
            />
          </div>
        </div>
      </div>
      
      <div className="reports-chart-container">
        <div className="reports-chart-scroll-wrapper">
          <div style={{ 
            height: '400px', 
            width: chartMinWidth,
            minWidth: '100%'
          }}>
            <Bar data={chartData} options={options} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyPerformanceChart;
